// Sprint 4.11.12 — Stock Adjustment Voucher (SAV) Phase 1
// ----------------------------------------------------------
// Two-tab page:
//   1. Vouchers   — list, create, view, edit (Draft), delete, admin reverse.
//   2. Register   — flattened approved adjustments (audit view) + xlsx/pdf export.
//
// Phase-1 scope: INVENTORY material lines only. No asset adjustments here
// (handled separately via Asset Scrap / Asset Transfer). Backend enforces
// approval-gated stock posting via _apply_sav_side_effects.
import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { PendingApprovalBadge, TakeActionButton, pendingDocIds } from "@/components/PendingApprovalBadge";
import { useApprovalPendingMap } from "@/hooks/useApprovalPendingMap";
import { BranchFilter } from "@/components/BranchFilter";
import {
  Plus, X, Trash2, Search, ClipboardList, FileDown, Eye, Pencil,
  AlertTriangle, Info, RefreshCw, Download,
} from "lucide-react";
import { inputCls, Field } from "./Projects";
import { SearchableSelect } from "@/components/SearchableSelect";
import { toast, Toaster } from "sonner";
import { useFormTab } from "@/hooks/useFormTab";
import { useViewOverlay } from "@/hooks/useViewOverlay";
import { useClientPagination } from "@/hooks/useClientPagination";
import UserLabel, { useUserDirectory } from "@/components/UserLabel";
import { PaginationControls } from "@/components/PaginationControls";
import { useAuth } from "@/contexts/AuthContext";

const ADJ_TYPES_FALLBACK = [
  "Physical Correction",
  "Damaged Material",
  "Missing Material",
  "Excess Material Found",
  "Opening Stock Correction",
  "Data Correction",
  "Other",
];

export default function StockAdjustmentsPage() {
  useUserDirectory();
  const [tab, setTab] = useState("vouchers");
  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Stores · Inventory"
        title="Stock Adjustment Voucher"
        subtitle="Approval-gated manual stock corrections. Auto-posts to batches, stock ledger & warehouse movements on final approval."
      />
      <div className="mb-4 flex items-center gap-1 border-b border-slate-200">
        <TabBtn active={tab === "vouchers"} onClick={() => setTab("vouchers")} testid="sav-tab-vouchers">Vouchers</TabBtn>
        <TabBtn active={tab === "register"} onClick={() => setTab("register")} testid="sav-tab-register">Register</TabBtn>
      </div>
      {tab === "vouchers" ? <VouchersTab /> : <RegisterTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition
        ${active ? "border-[#F97316] text-[#F97316]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Tab 1: VOUCHERS
// ─────────────────────────────────────────────────────────────
function VouchersTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [filterBranch, setFilterBranch] = useState("");
  const [filterWarehouse, setFilterWarehouse] = useState("");
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [search, setSearch] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const openFormTab = useFormTab();

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterBranch) params.set("branch_id", filterBranch);
      if (filterWarehouse) params.set("warehouse_id", filterWarehouse);
      if (filterStatus) params.set("status", filterStatus);
      if (filterType) params.set("adjustment_type", filterType);
      const r = await api.get(`/stock-adjustments?${params.toString()}`);
      setList((r.data?.items) || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }

  useEffect(() => {
    const url = filterBranch ? `/warehouses/picker?branch_id=${filterBranch}` : `/warehouses/picker`;
    api.get(url).then(r => setWarehouseOptions(Array.isArray(r.data) ? r.data : []))
       .catch(() => setWarehouseOptions([]));
    setFilterWarehouse("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBranch]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBranch, filterWarehouse, filterStatus, filterType]);

  const pendingIds = useMemo(() => pendingDocIds(list), [list]);
  const pendingMap = useApprovalPendingMap("stock_adjustment_voucher", pendingIds);

  const openNew = () => openFormTab({
    path: "/stock-adjustments/new",
    label: "New Stock Adjustment",
    icon: Plus,
    parent: "/stock-adjustments",
    onSaved: load,
    render: ({ close, saved, viewList }) => (
      <StockAdjustmentForm onClose={close} onSaved={() => { saved(); load(); }} onViewList={viewList} />
    ),
  });

  const openEdit = (v) => openFormTab({
    path: `/stock-adjustments/${v.id}/edit`,
    label: `Edit ${v.voucher_number || v.id.slice(0, 8)}`,
    icon: Pencil,
    parent: "/stock-adjustments",
    onSaved: load,
    render: ({ close, saved, viewList }) => (
      <StockAdjustmentForm existing={v} onClose={close} onSaved={() => { saved(); load(); }} onViewList={viewList} />
    ),
  });

  async function deleteVoucher(v) {
    if (v.posted) {
      toast.error("Cannot delete a posted voucher. Cancel approval first.");
      return;
    }
    if (!window.confirm(`Permanently delete voucher ${v.voucher_number}?`)) return;
    try {
      await api.delete(`/stock-adjustments/${v.id}`);
      toast.success(`${v.voucher_number} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  async function cancelApproval(v) {
    const msg =
      `Reverse stock adjustment ${v.voucher_number}?\n\n` +
      `• Each batch will be re-set to its pre-adjustment quantity.\n` +
      `• Stock ledger + warehouse-movement audit rows will be deleted.\n` +
      `• Voucher will return to Draft so you can edit / delete it.\n\n` +
      `Continue?`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/stock-adjustments/${v.id}/cancel-approval`);
      toast.success(`${v.voucher_number} reversed — back to Draft`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reverse failed");
    }
  }

  async function downloadVoucherPdf(v) {
    try {
      const res = await api.get(`/stock-adjustments/${v.id}/voucher-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${v.voucher_number}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(v => [
      v.voucher_number, v.branch_name, v.warehouse_name, v.adjustment_type,
      v.reason, v.remarks, v.reference_document, v.status,
      (v.adjustment_date || "").slice(0, 10),
    ].some(x => (x || "").toString().toLowerCase().includes(q)));
  }, [list, search]);

  const { paged, page, pageSize, total, setPage, setPageSize } = useClientPagination(filtered, { initialPageSize: 50 });

  return (
    <div>
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="sav-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by voucher / type / reason / status…"
            className="border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316] min-w-[300px]" />
        </div>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="sav-branch-filter" />
        <select data-testid="sav-warehouse-filter" value={filterWarehouse}
          onChange={e => setFilterWarehouse(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm min-w-[180px]">
          <option value="">All warehouses</option>
          {warehouseOptions.map(w => (
            <option key={w.id} value={w.id}>{w.warehouse_code} — {w.warehouse_name}</option>
          ))}
        </select>
        <select data-testid="sav-type-filter" value={filterType} onChange={e => setFilterType(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">All types</option>
          {ADJ_TYPES_FALLBACK.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="sav-status-filter" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">All status</option>
          <option value="Draft">Draft</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
        <button data-testid="sav-reload-btn" onClick={load} className="text-slate-500 hover:text-[#F97316] p-1.5" title="Reload">
          <RefreshCw size={14} />
        </button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">
            <span className="text-slate-400 mr-1">Showing:</span>
            <span className="font-bold text-slate-800 tabular">{filtered.length}</span>
            <span className="text-slate-400 mx-1">/</span>
            <span className="text-slate-600 tabular">{list.length}</span>
          </span>
          <button data-testid="new-sav-btn" onClick={openNew}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Plus size={14} /> New Adjustment
          </button>
        </div>
      </div>

      {list.length === 0 ? (
        <EmptyState title={loading ? "Loading…" : "No stock adjustments yet"} icon={ClipboardList}
          hint={loading ? undefined : "Use 'New Adjustment' to record a physical correction, damage write-off, or opening-stock entry."} />
      ) : filtered.length === 0 ? (
        <EmptyState title="No matches" hint="Clear filters to see all vouchers." />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full table-fixed" data-testid="sav-table">
            <colgroup>
              <col style={{ width: "13%" }} />{/* Voucher / Date */}
              <col style={{ width: "15%" }} />{/* Branch / Warehouse */}
              <col style={{ width: "18%" }} />{/* Type / Reason */}
              <col style={{ width: "6%"  }} />{/* Lines */}
              <col style={{ width: "10%" }} />{/* Net Δ Qty */}
              <col style={{ width: "10%" }} />{/* Value */}
              <col style={{ width: "16%" }} />{/* Status */}
              <col style={{ width: "12%" }} />{/* Actions */}
            </colgroup>
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>
                {["Voucher · Date", "Branch · Warehouse", "Type · Reason", "Lines", "Net Δ Qty", "Value", "Status", ""].map((h, i) =>
                  <th key={h || i}
                      className={`text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-3 ${
                        h === "Lines" ? "text-center" : (h === "Net Δ Qty" || h === "Value") ? "text-right" : "text-left"
                      }`}>{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paged.map(v => {
                const lines = v.line_items || [];
                const netQty = lines.reduce((s, l) => s + (Number(l.adjustment_qty) || 0), 0);
                const value  = lines.reduce((s, l) => s + ((Number(l.adjustment_qty) || 0) * (Number(l.unit_rate) || 0)), 0);
                return (
                  <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`sav-row-${v.id}`}>
                    <td className="py-2.5 px-3 align-top">
                      <button onClick={() => setViewing(v)} data-testid={`view-sav-${v.id}`}
                        className="font-mono text-[11px] font-bold text-[#F97316] hover:underline block leading-tight text-left">
                        {v.voucher_number || v.id.slice(0, 8)}
                      </button>
                      <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{(v.adjustment_date || "").slice(0, 10)}</div>
                    </td>
                    <td className="py-2.5 px-3 align-top">
                      <div className="text-[11px] text-slate-700 truncate" title={v.branch_name}>{v.branch_name || "—"}</div>
                      <div className="text-[10px] font-bold text-slate-500 truncate" title={v.warehouse_name}>{v.warehouse_name || "—"}</div>
                    </td>
                    <td className="py-2.5 px-3 align-top">
                      <div className="text-[11px] font-bold text-slate-700 truncate" title={v.adjustment_type}>{v.adjustment_type || "—"}</div>
                      <div className="text-[10px] text-slate-500 truncate" title={v.reason}>{v.reason || "—"}</div>
                    </td>
                    <td className="py-2.5 px-3 text-center text-[11px] text-slate-700 align-top">{lines.length}</td>
                    <td className={`py-2.5 px-3 text-right tabular align-top text-[11px] font-bold ${netQty >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {netQty > 0 ? "+" : ""}{netQty.toFixed(2)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular text-[11px] text-slate-700 align-top">{formatINR(Math.abs(value))}</td>
                    <td className="py-2.5 px-3 align-top">
                      <StatusBadge status={v.status} />
                      {pendingMap[v.id] && <div className="mt-1"><PendingApprovalBadge req={pendingMap[v.id]} /></div>}
                      <TakeActionButton req={pendingMap[v.id]} onActioned={load} />
                      {v.posted && isAdmin && v.status === "Approved" && (
                        <button data-testid={`reverse-sav-${v.id}`} onClick={() => cancelApproval(v)}
                          className="block mt-1 text-[10px] uppercase tracking-wider font-bold text-rose-600 hover:text-rose-800">
                          Reverse Adj.
                        </button>
                      )}
                    </td>
                    <td className="py-2.5 px-3 align-top">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => downloadVoucherPdf(v)} title="Download PDF"
                          data-testid={`pdf-sav-${v.id}`}
                          className="p-1 text-slate-500 hover:text-[#F97316]">
                          <FileDown size={13} />
                        </button>
                        <button onClick={() => setViewing(v)} title="View"
                          data-testid={`view-icon-sav-${v.id}`}
                          className="p-1 text-slate-500 hover:text-[#F97316]">
                          <Eye size={13} />
                        </button>
                        {!v.posted && (
                          <button onClick={() => openEdit(v)} title="Edit (Draft only)"
                            data-testid={`edit-sav-${v.id}`}
                            className="p-1 text-slate-500 hover:text-blue-600">
                            <Pencil size={13} />
                          </button>
                        )}
                        {!v.posted && isAdmin && (
                          <button onClick={() => deleteVoucher(v)} title="Delete"
                            data-testid={`delete-sav-${v.id}`}
                            className="p-1 text-slate-500 hover:text-rose-600">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <PaginationControls page={page} pageSize={pageSize} total={total}
            onPage={setPage} onPageSize={setPageSize}
            pageSizeOptions={[25, 50, 100, 200]} />
        </Card>
      )}

      {viewing && <SavView v={viewing} onClose={() => setViewing(null)} onDownload={() => downloadVoucherPdf(viewing)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// View modal (read-only)
// ─────────────────────────────────────────────────────────────
function SavView({ v, onClose, onDownload }) {
  const lines = v.line_items || [];
  // Sprint 4.11.13 — scroll-lock + dynamic top offset via shared hook.
  const topOffset = useViewOverlay();
  return (
    <div
      className="fixed left-0 lg:left-64 right-0 bottom-0 bg-white z-40 overflow-y-auto"
      style={{ top: `${topOffset}px` }}
      data-testid="sav-view-overlay">
      <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
        <div>
          <h2 className="font-display text-2xl font-bold">{v.voucher_number || "Stock Adjustment"}</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {(v.adjustment_date || "").slice(0, 10)} · {v.branch_name} · {v.warehouse_name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="sav-view-download-pdf" onClick={onDownload}
            className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-[#F97316] text-white hover:bg-[#EA580C] flex items-center gap-1.5">
            <Download size={13} /> Download PDF
          </button>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100" data-testid="sav-view-close"><X size={18} /></button>
        </div>
      </div>
      <div className="px-8 py-6 space-y-5 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Info4 label="Adjustment Type" value={v.adjustment_type} bold />
          <Info4 label="Status" valueNode={<StatusBadge status={v.status} />} />
          <Info4 label="Reference Doc" value={v.reference_document || "—"} />
          <Info4 label="Posted" value={v.posted ? "YES · stock updated" : "NO · pending approval"} />
        </div>
        <Info4 label="Reason" value={v.reason} bold full />
        <Info4 label="Remarks" value={v.remarks} full />
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>{["#", "Item", "Batch", "Before Qty", "Δ Adjustment", "After Qty", "Unit", "Rate", "Line Remarks"].map(h =>
                <th key={h} className={`text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3
                    ${["Before Qty", "Δ Adjustment", "After Qty", "Rate"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {lines.map((li, i) => {
                const adj = Number(li.adjustment_qty) || 0;
                return (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 px-3 text-xs text-slate-500">{i + 1}</td>
                    <td className="py-2 px-3">
                      <div className="text-[11px] font-bold text-slate-800">{li.item_name || "—"}</div>
                      <div className="text-[10px] font-mono text-slate-500">{li.item_code || ""}</div>
                    </td>
                    <td className="py-2 px-3 text-[10px] font-mono text-slate-500">{li.batch_code || "—"}</td>
                    <td className="py-2 px-3 text-xs text-right tabular">{Number(li.before_qty || 0).toFixed(2)}</td>
                    <td className={`py-2 px-3 text-xs text-right tabular font-bold ${adj >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {adj > 0 ? "+" : ""}{adj.toFixed(2)}
                    </td>
                    <td className="py-2 px-3 text-xs text-right tabular">{Number(li.after_qty || 0).toFixed(2)}</td>
                    <td className="py-2 px-3 text-xs text-slate-600">{li.unit || ""}</td>
                    <td className="py-2 px-3 text-xs text-right tabular">{formatINR(Number(li.unit_rate || 0))}</td>
                    <td className="py-2 px-3 text-[10px] text-slate-500">{li.remarks || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}

function Info4({ label, value, valueNode, bold, full }) {
  return (
    <div className={full ? "col-span-2 md:col-span-4" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
      {valueNode || <div className={`mt-0.5 ${bold ? "font-bold text-slate-800" : "text-slate-700"} text-sm`}>{value || "—"}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CREATE / EDIT form
// ─────────────────────────────────────────────────────────────
function StockAdjustmentForm({ existing, onClose, onViewList, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const isEdit = !!existing;
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [types, setTypes] = useState(ADJ_TYPES_FALLBACK);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(() => {
    if (existing) {
      return {
        adjustment_date: (existing.adjustment_date || today).slice(0, 10),
        branch_id: existing.branch_id || "",
        branch_name: existing.branch_name || "",
        warehouse_id: existing.warehouse_id || "",
        warehouse_name: existing.warehouse_name || "",
        adjustment_type: existing.adjustment_type || "",
        reason: existing.reason || "",
        reference_document: existing.reference_document || "",
        remarks: existing.remarks || "",
        line_items: (existing.line_items || []).map(l => ({ ...l })),
      };
    }
    return {
      adjustment_date: today,
      branch_id: "", branch_name: "",
      warehouse_id: "", warehouse_name: "",
      adjustment_type: "",
      reason: "",
      reference_document: "",
      remarks: "",
      line_items: [],
    };
  });

  useEffect(() => {
    api.get("/branches").then(r => setBranches(r.data || []));
    // Use the slim `/items/_pickable` endpoint — same pattern as
    // Estimates / Sales Invoice / Issue Voucher / Inventory pickers.
    // The generic `/items` CRUD list caps at PAGE_MAX=500 and sorts
    // by `created_at` DESC, which silently hides older items from
    // any tenant with >500 items (bug: "RCPL277 in stock but missing
    // in Stock Adjustment picker"). `_pickable` returns every active
    // item sorted by `item_code` ASC with no pagination cap.
    //
    // Backend projection uses field `name` (not `item_name`). We
    // normalize it into `item_name` on receive so the rest of the
    // form (getLabel, getSearchText, pickItem, save payload) keeps
    // working with the `item_name` convention already used elsewhere
    // on this page.
    api.get("/items/_pickable").then(r => {
      const all = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      // Phase-1: only Inventory items (Material). Asset/Service excluded.
      setItems(
        all
          .filter(i => (i.item_type || "Inventory") === "Inventory")
          .map(i => ({ ...i, item_name: i.item_name || i.name || "" }))
      );
    });
    api.get("/stock-adjustments/meta").then(r => {
      const t = r.data?.adjustment_types;
      if (Array.isArray(t) && t.length) setTypes(t);
    }).catch(() => {});
  }, []);

  // Warehouses follow branch.
  useEffect(() => {
    if (!form.branch_id) { setWarehouses([]); return; }
    api.get(`/warehouses/picker?branch_id=${form.branch_id}`)
      .then(r => setWarehouses(Array.isArray(r.data) ? r.data : []))
      .catch(() => setWarehouses([]));
  }, [form.branch_id]);

  // Batches follow branch (+ include depleted=false so zero-qty rows don't clutter
  // the picker; positive adjustments may still create a new ADJ batch).
  useEffect(() => {
    if (!form.branch_id) { setBatches([]); return; }
    api.get(`/inventory/batches?branch_id=${form.branch_id}&include_depleted=false`)
      .then(r => setBatches(r.data || []))
      .catch(() => setBatches([]));
  }, [form.branch_id]);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const setBranch = (bid) => {
    const br = branches.find(b => b.id === bid);
    setForm(p => ({ ...p,
      branch_id: bid, branch_name: br?.branch_name || "",
      warehouse_id: "", warehouse_name: "",
      line_items: [],
    }));
  };

  const setWarehouse = (wid) => {
    const w = warehouses.find(x => x.id === wid);
    setForm(p => ({ ...p,
      warehouse_id: wid, warehouse_name: w?.warehouse_name || "",
      line_items: p.line_items.map(li => ({ ...li, batch_id: "", batch_code: "" })),
    }));
  };

  function addLine() {
    setForm(p => ({ ...p, line_items: [...p.line_items, {
      item_id: "", item_code: "", item_name: "", unit: "",
      batch_id: "", batch_code: "",
      // adjustment_qty starts as an empty STRING (not 0) so users can
      // type "-40" directly without having to backspace over a leading
      // "0". Downstream code already tolerates the string via Number()
      // coercion; the setAdjQty controller keeps state as a string
      // while typing and hands a number back to after_qty.
      before_qty: 0, adjustment_qty: "", after_qty: 0,
      unit_rate: 0, remarks: "",
    }]}));
  }
  function updLine(idx, patch) {
    setForm(p => ({ ...p, line_items: p.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li) }));
  }
  function removeLine(idx) {
    setForm(p => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }));
  }

  function pickItem(idx, itemId) {
    // Empty itemId == user clicked the X clear-button → reset the line.
    if (!itemId) {
      updLine(idx, {
        item_id: "", item_code: "", item_name: "", unit: "",
        batch_id: "", batch_code: "", before_qty: 0, after_qty: 0,
        unit_rate: 0,
      });
      return;
    }
    const it = items.find(x => x.id === itemId);
    if (!it) return;
    updLine(idx, {
      item_id: it.id, item_code: it.item_code, item_name: it.item_name,
      unit: it.unit || "",
      batch_id: "", batch_code: "", before_qty: 0, after_qty: 0,
    });
  }

  function pickBatch(idx, batchId) {
    const li = form.line_items[idx];
    const b = batches.find(x => x.id === batchId);
    if (!b) {
      updLine(idx, { batch_id: "", batch_code: "", before_qty: 0, after_qty: li.adjustment_qty });
      return;
    }
    updLine(idx, {
      batch_id: b.id, batch_code: b.batch_code,
      item_id: li.item_id || b.item_id,
      item_code: li.item_code || b.item_code,
      item_name: li.item_name || b.item_name,
      unit: li.unit || b.unit,
      unit_rate: li.unit_rate || b.unit_rate || 0,
      before_qty: Number(b.remaining_qty || 0),
      after_qty: Number(b.remaining_qty || 0) + Number(li.adjustment_qty || 0),
    });
  }

  function setAdjQty(idx, qty) {
    // We use <input type="text" inputMode="decimal"> for this field —
    // native <input type="number"> silently swallows the intermediate
    // "-" character on many browsers before the user can complete
    // typing e.g. "-40", making it look like the field "does not accept
    // negatives" when it actually does.
    //
    // Accept:
    //   ""            (empty)
    //   "-"           (typing a negative in progress)
    //   "-.", "."     (typing a decimal in progress)
    //   "-40", "-40.5", "0.5", "42" (valid numbers)
    // Reject anything with letters or extra symbols.
    if (qty !== "" && !/^-?\d*\.?\d*$/.test(qty)) return;   // drop invalid keystrokes
    const li = form.line_items[idx];
    const numeric = (qty === "" || qty === "-" || qty === "-." || qty === ".") ? 0 : Number(qty);
    const safeNumeric = Number.isFinite(numeric) ? numeric : 0;
    updLine(idx, {
      adjustment_qty: qty,
      after_qty: Number(li.before_qty || 0) + safeNumeric,
    });
  }

  // Item picker is filtered to the chosen warehouse's stock when a warehouse
  // is picked. Positive adjustments on items with NO existing batch in the
  // warehouse are still allowed (server creates a synthetic ADJ- batch).
  const itemOptions = useMemo(() => items, [items]);

  // Pre-compute available stock per item for the currently-chosen warehouse
  // so the item picker can render "(Available: 25.50 Nos)" inline — helps
  // users decide which item to pick without leaving the form.
  const itemStockMap = useMemo(() => {
    const map = {};
    const pool = form.warehouse_id
      ? batches.filter(b => (b.warehouse_id || "") === form.warehouse_id)
      : batches;
    for (const b of pool) {
      const k = b.item_id;
      if (!k) continue;
      map[k] = (map[k] || 0) + Number(b.remaining_qty || 0);
    }
    return map;
  }, [batches, form.warehouse_id]);

  const itemStockLabel = (item) => {
    const qty = itemStockMap[item.id] || 0;
    const u = item.unit || "";
    return qty > 0
      ? `Available: ${qty.toFixed(2)} ${u}`
      : `Available: 0 ${u} · synthetic batch will be created on +adj`;
  };

  // Batch picker filtered by item + warehouse.
  const batchOptionsFor = (line) => {
    let pool = batches;
    if (form.warehouse_id) pool = pool.filter(b => (b.warehouse_id || "") === form.warehouse_id);
    if (line.item_id) pool = pool.filter(b => b.item_id === line.item_id);
    return pool;
  };

  async function save() {
    // Client-side validation matches backend.
    if (!form.branch_id) return toast.error("Pick a branch.");
    if (!form.warehouse_id) return toast.error("Pick a warehouse.");
    if (!form.adjustment_type) return toast.error("Pick an adjustment type.");
    if (!form.reason.trim()) return toast.error("Reason is mandatory.");
    if (!form.remarks.trim()) return toast.error("Remarks are mandatory.");
    if (!form.line_items.length) return toast.error("Add at least one line.");
    for (let i = 0; i < form.line_items.length; i++) {
      const li = form.line_items[i];
      if (!li.item_id) return toast.error(`Line ${i + 1}: pick an item.`);
      if (!Number(li.adjustment_qty)) return toast.error(`Line ${i + 1}: adjustment quantity cannot be zero.`);
      if (Number(li.adjustment_qty) < 0 && !li.batch_id) {
        return toast.error(`Line ${i + 1}: negative adjustments require a specific batch.`);
      }
    }
    setSaving(true);
    const t = toast.loading(isEdit ? "Saving…" : "Submitting for approval…");
    try {
      const payload = {
        adjustment_date: form.adjustment_date,
        branch_id: form.branch_id,
        branch_name: form.branch_name,
        warehouse_id: form.warehouse_id,
        adjustment_type: form.adjustment_type,
        reason: form.reason.trim(),
        reference_document: form.reference_document.trim(),
        remarks: form.remarks.trim(),
        line_items: form.line_items.map(li => ({
          item_id: li.item_id,
          batch_id: li.batch_id || "",
          adjustment_qty: Number(li.adjustment_qty || 0),
          unit_rate: Number(li.unit_rate || 0),
          remarks: (li.remarks || "").trim(),
        })),
      };
      if (isEdit) {
        await api.put(`/stock-adjustments/${existing.id}`, payload);
        toast.success(`Voucher updated — awaiting fresh approval`, { id: t });
      } else {
        const r = await api.post("/stock-adjustments", payload);
        toast.success(`${r.data?.voucher_number || "Voucher"} submitted for approval`, { id: t });
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed", { id: t });
    } finally { setSaving(false); }
  }

  const netQty = form.line_items.reduce((s, l) => s + (Number(l.adjustment_qty) || 0), 0);
  const netVal = form.line_items.reduce((s, l) => s + ((Number(l.adjustment_qty) || 0) * (Number(l.unit_rate) || 0)), 0);
  const isFormValid =
    !!form.branch_id && !!form.warehouse_id && !!form.adjustment_type &&
    form.reason.trim() && form.remarks.trim() &&
    form.line_items.length > 0 &&
    form.line_items.every(li => li.item_id && Number(li.adjustment_qty) !== 0);

  return (
    <div className="bg-slate-50 min-h-screen pb-16">
      <Toaster richColors position="top-right" />
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20 px-8 py-4 shadow-sm flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold">
            {isEdit ? `Edit Stock Adjustment — ${existing.voucher_number}` : "New Stock Adjustment"}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {isEdit
              ? "Saving will re-queue the voucher through approval. Posted vouchers must be reversed first."
              : "On save, the voucher is sent through the approval workflow. Stock is NOT modified until final approval."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="sav-form-view-list" onClick={onViewList} className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold text-slate-600 hover:text-[#F97316]">View List</button>
          <button data-testid="sav-form-cancel" onClick={onClose} className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold text-slate-600 hover:text-rose-600">Cancel</button>
          <button data-testid="sav-form-save" onClick={save} disabled={saving || !isFormValid}
            title={!isFormValid ? "Fill branch · warehouse · type · reason · remarks · at least one line" : undefined}
            className="bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 text-xs uppercase tracking-wider font-bold">
            {saving ? "Saving…" : (isEdit ? "Save Changes" : "Submit for Approval")}
          </button>
        </div>
      </div>

      <div className="px-8 py-6 max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <Card>
          <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Adjustment Date" required>
              <input type="date" data-testid="sav-date" value={form.adjustment_date}
                onChange={e => setF("adjustment_date", e.target.value)}
                className={inputCls} />
            </Field>
            <Field label="Branch" required>
              <select data-testid="sav-branch" value={form.branch_id} onChange={e => setBranch(e.target.value)} className={inputCls}>
                <option value="">— Select branch —</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.branch_code} — {b.branch_name}</option>)}
              </select>
            </Field>
            <Field label="Warehouse" required>
              <select data-testid="sav-warehouse" value={form.warehouse_id} disabled={!form.branch_id}
                onChange={e => setWarehouse(e.target.value)} className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-400`}>
                <option value="">{form.branch_id ? "— Select warehouse —" : "Pick branch first"}</option>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.warehouse_code} — {w.warehouse_name}</option>)}
              </select>
            </Field>

            <Field label="Adjustment Type" required>
              <select data-testid="sav-type" value={form.adjustment_type}
                onChange={e => setF("adjustment_type", e.target.value)} className={inputCls}>
                <option value="">— Select type —</option>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Reference Document">
              <input data-testid="sav-ref-doc" value={form.reference_document}
                onChange={e => setF("reference_document", e.target.value)}
                placeholder="Optional — physical count sheet, audit memo no., etc."
                className={inputCls} />
            </Field>
            <div></div>

            <div className="md:col-span-3">
              <Field label="Reason (one-line summary)" required>
                <input data-testid="sav-reason" value={form.reason}
                  onChange={e => setF("reason", e.target.value)}
                  placeholder="E.g. Damaged on receipt; missing during physical count; opening balance correction"
                  className={inputCls} />
              </Field>
            </div>
            <div className="md:col-span-3">
              <Field label="Detailed Remarks" required>
                <textarea data-testid="sav-remarks" value={form.remarks} rows={3}
                  onChange={e => setF("remarks", e.target.value)}
                  placeholder="Mandatory — explain root cause, references, follow-up actions taken."
                  className={`${inputCls} resize-y`} />
              </Field>
            </div>
          </div>
        </Card>

        {/* Lines */}
        <Card>
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="font-display text-base font-bold">Adjustment Lines</h3>
              <p className="text-[11px] text-slate-500">Positive (+) increases stock, Negative (−) decreases. Negative requires picking a specific batch.</p>
            </div>
            <button data-testid="sav-add-line" onClick={addLine} disabled={!form.warehouse_id}
              className="bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 px-3 py-1.5 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 border border-slate-300">
              <Plus size={12} /> Add Line
            </button>
          </div>
          {!form.warehouse_id ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              <Info size={20} className="inline-block mr-2 text-slate-400" />
              Pick a branch and warehouse to start adding lines.
            </div>
          ) : form.line_items.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              No lines yet. Click <span className="font-bold">+ Add Line</span> to enter one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>{["#", "Item", "Batch (Optional)", "Before Qty", "Δ Adjustment", "After Qty", "Rate", "Line Remarks", ""].map(h =>
                    <th key={h} className={`text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-2
                        ${["Before Qty", "Δ Adjustment", "After Qty", "Rate"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {form.line_items.map((li, idx) => {
                    const adj = Number(li.adjustment_qty) || 0;
                    return (
                      <tr key={idx} className="border-b border-slate-100" data-testid={`sav-line-row-${idx}`}>
                        <td className="py-2 px-2 text-xs text-slate-500 align-top">{idx + 1}</td>
                        <td className="py-2 px-2 min-w-[240px] align-top">
                          <SearchableSelect
                            value={li.item_id}
                            onChange={(id) => pickItem(idx, id)}
                            items={itemOptions}
                            getKey={(r) => r.id}
                            getLabel={(r) => `${r.item_code || ""} · ${r.item_name || ""}`}
                            getSubLabel={(r) => `${itemStockLabel(r)} · ${r.category || "General"}`}
                            getSearchText={(r) => `${r.item_code || ""} ${r.item_name || ""} ${r.unit || ""}`.toLowerCase()}
                            placeholder="Search & pick item…"
                            allowClear
                            testid={`sav-item-${idx}`}
                          />
                        </td>
                        <td className="py-2 px-2 min-w-[180px] align-top">
                          <SearchableSelect
                            value={li.batch_id}
                            onChange={(id) => pickBatch(idx, id)}
                            items={batchOptionsFor(li)}
                            getKey={(r) => r.id}
                            getLabel={(r) => r.batch_code || r.id.slice(0, 8)}
                            getSubLabel={(r) => `Qty ${Number(r.remaining_qty || 0).toFixed(2)} ${r.unit || ""} · ₹${Number(r.unit_rate || 0).toFixed(2)}`}
                            getSearchText={(r) => `${r.batch_code || ""} ${r.unit || ""}`.toLowerCase()}
                            placeholder={li.item_id ? "Pick existing batch (optional)" : "Pick item first"}
                            disabled={!li.item_id}
                            allowClear
                            testid={`sav-batch-${idx}`}
                          />
                        </td>
                        <td className="py-2 px-2 text-right tabular text-xs text-slate-600 align-top">{Number(li.before_qty || 0).toFixed(2)}</td>
                        <td className="py-2 px-2 align-top">
                          <input type="text" inputMode="decimal" pattern="-?\d*\.?\d*"
                            data-testid={`sav-adj-qty-${idx}`}
                            value={li.adjustment_qty}
                            onChange={e => setAdjQty(idx, e.target.value)}
                            placeholder="e.g. -40 for missing stock"
                            className={`${inputCls} text-right tabular font-bold ${adj > 0 ? "text-emerald-700" : adj < 0 ? "text-rose-700" : ""}`} />
                        </td>
                        <td className={`py-2 px-2 text-right tabular text-xs font-bold align-top ${(Number(li.after_qty || 0) >= 0) ? "text-slate-700" : "text-rose-600"}`}>
                          {Number(li.after_qty || 0).toFixed(2)}
                        </td>
                        <td className="py-2 px-2 align-top">
                          <input type="number" step="0.01" data-testid={`sav-rate-${idx}`}
                            value={li.unit_rate}
                            onChange={e => updLine(idx, { unit_rate: Number(e.target.value || 0) })}
                            className={`${inputCls} text-right tabular`} />
                        </td>
                        <td className="py-2 px-2 align-top">
                          <input data-testid={`sav-line-remarks-${idx}`} value={li.remarks}
                            onChange={e => updLine(idx, { remarks: e.target.value })}
                            placeholder="Optional"
                            className={inputCls} />
                        </td>
                        <td className="py-2 px-2 align-top">
                          <button onClick={() => removeLine(idx)} data-testid={`sav-line-del-${idx}`}
                            className="text-slate-400 hover:text-rose-600 p-1">
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                    <td colSpan={4} className="py-2 px-2 text-right text-[10px] uppercase tracking-wider font-bold text-slate-500">Net</td>
                    <td className={`py-2 px-2 text-right tabular text-xs font-bold ${netQty >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="sav-net-qty">
                      {netQty > 0 ? "+" : ""}{netQty.toFixed(2)}
                    </td>
                    <td></td>
                    <td className="py-2 px-2 text-right tabular text-xs font-bold text-slate-700" data-testid="sav-net-value">
                      {formatINR(Math.abs(netVal))}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>

        <div className="bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-900 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <strong>Phase 1 — Inventory only.</strong> SAV affects ONLY the chosen warehouse + branch.
            Stock changes are deferred until the approval matrix completes. Posted adjustments can be reversed by Admin from the list view.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Tab 2: REGISTER (flat list of approved lines + exports)
// ─────────────────────────────────────────────────────────────
function RegisterTab() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [filterBranch, setFilterBranch] = useState("");
  const [filterWarehouse, setFilterWarehouse] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const url = filterBranch ? `/warehouses/picker?branch_id=${filterBranch}` : `/warehouses/picker`;
    api.get(url).then(r => setWarehouses(Array.isArray(r.data) ? r.data : [])).catch(() => setWarehouses([]));
    setFilterWarehouse("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBranch]);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterBranch) params.set("branch_id", filterBranch);
      if (filterWarehouse) params.set("warehouse_id", filterWarehouse);
      if (filterType) params.set("adjustment_type", filterType);
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      const r = await api.get(`/stock-adjustments/register/rows?${params.toString()}`);
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load register");
    } finally { setLoading(false); }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function exportFile(kind) {
    const params = new URLSearchParams();
    if (filterBranch) params.set("branch_id", filterBranch);
    if (filterWarehouse) params.set("warehouse_id", filterWarehouse);
    if (filterType) params.set("adjustment_type", filterType);
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    try {
      const url = `/stock-adjustments/register/export.${kind}?${params.toString()}`;
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `stock_adjustment_register.${kind}`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  }

  const { paged, page, pageSize, total, setPage, setPageSize } = useClientPagination(rows, { initialPageSize: 50 });

  return (
    <div>
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">From</span>
        <input type="date" data-testid="register-from" value={from} onChange={e => setFrom(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">To</span>
        <input type="date" data-testid="register-to" value={to} onChange={e => setTo(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm" />
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="register-branch-filter" />
        <select data-testid="register-warehouse-filter" value={filterWarehouse}
          onChange={e => setFilterWarehouse(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm min-w-[160px]">
          <option value="">All warehouses</option>
          {warehouses.map(w => <option key={w.id} value={w.id}>{w.warehouse_code} — {w.warehouse_name}</option>)}
        </select>
        <select data-testid="register-type-filter" value={filterType} onChange={e => setFilterType(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">All types</option>
          {ADJ_TYPES_FALLBACK.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <button data-testid="register-apply" onClick={load}
          className="bg-slate-800 hover:bg-slate-900 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold">
          Apply
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="register-export-xlsx" onClick={() => exportFile("xlsx")}
            className="px-3 py-2 text-xs uppercase tracking-wider font-bold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5">
            <Download size={13} /> Excel
          </button>
          <button data-testid="register-export-pdf" onClick={() => exportFile("pdf")}
            className="px-3 py-2 text-xs uppercase tracking-wider font-bold bg-rose-600 hover:bg-rose-700 text-white flex items-center gap-1.5">
            <Download size={13} /> PDF
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState title={loading ? "Loading register…" : "No approved adjustments in this range"}
          icon={ClipboardList}
          hint="Adjust filters or widen the date range." />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm" data-testid="register-table">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>{["Voucher", "Date", "Branch · WH", "Item", "Batch", "Before", "Δ", "After", "Type · Reason", "Approved By"].map(h =>
                <th key={h} className={`text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-3
                    ${["Before", "Δ", "After"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {paged.map((r, i) => {
                const d = Number(r.adjustment_qty || 0);
                return (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-2 px-3 font-mono text-[11px] font-bold text-[#F97316]">{r.voucher_number}</td>
                    <td className="py-2 px-3 text-[11px] text-slate-600">{r.adjustment_date}</td>
                    <td className="py-2 px-3 text-[11px]">
                      <div className="text-slate-700">{r.branch_name}</div>
                      <div className="text-slate-500">{r.warehouse_name}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="text-[11px] font-bold text-slate-800">{r.item_name}</div>
                      <div className="text-[10px] font-mono text-slate-500">{r.item_code}</div>
                    </td>
                    <td className="py-2 px-3 font-mono text-[10px] text-slate-500">{r.batch_code || "—"}</td>
                    <td className="py-2 px-3 text-right tabular text-[11px]">{Number(r.before_qty || 0).toFixed(2)}</td>
                    <td className={`py-2 px-3 text-right tabular text-[11px] font-bold ${d >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {d > 0 ? "+" : ""}{d.toFixed(2)}
                    </td>
                    <td className="py-2 px-3 text-right tabular text-[11px]">{Number(r.after_qty || 0).toFixed(2)}</td>
                    <td className="py-2 px-3 text-[10px]">
                      <div className="font-bold text-slate-700">{r.adjustment_type}</div>
                      <div className="text-slate-500">{r.reason}</div>
                    </td>
                    <td className="py-2 px-3 text-[10px] text-slate-500">
                      <UserLabel value={r.approved_by} fallback="—" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <PaginationControls page={page} pageSize={pageSize} total={total}
            onPage={setPage} onPageSize={setPageSize}
            pageSizeOptions={[25, 50, 100, 200]} />
        </Card>
      )}
    </div>
  );
}
